import simulados from '@/dados-simulados/parametrizacao.json';

/**
 * A ÚNICA porta de entrada do dado simulado nas telas de parametrização.
 *
 * Restam aqui só os cadastros de documentos e financeiro (requisitos, apólices,
 * bases de cálculo de multa, glosas, capacidade, documentos de pagamento e a
 * validação do backfill), que ainda não têm rota na API. Os doze cadastros de
 * domínio, as seguradoras e as escolas saíram: leem e gravam pela API em
 * `cadastros.dados.ts`, e o arquivo simulado não os traz mais.
 *
 * Quando o endpoint de um cadastro que resta nascer, `carregarDominio` dele passa
 * a consultar o servidor, a chave sai do JSON e do `LEIA-ME.md`, e nenhuma tela
 * muda. É também o que cumpre a condição 2 da exceção declarada no CLAUDE.md —
 * nada além daqui importa de `dados-simulados/`.
 */

/** Toda linha de domínio tem identidade e situação; o resto varia por domínio. */
export interface LinhaDeDominio {
  readonly id?: string;
  readonly slug?: string;
  readonly situacao?: string;
  readonly [coluna: string]: unknown;
}

/** Nome de cada coleção do arquivo simulado. */
export type ChaveDeDominio = Exclude<keyof typeof simulados, '_leia'>;

/**
 * Atraso curto antes de responder.
 *
 * Não é enfeite: sem ele o estado "carregando" nunca aparece em tela, e um
 * estado que não se consegue ver é um estado que não se consegue conferir.
 * A tela real terá latência de rede; esta tem 250 ms.
 */
const LATENCIA_MS = 250;

/**
 * Domínios que devem falhar de propósito, para conferir o estado de erro.
 *
 * Vazio em condição normal. Preencher só ao inspecionar a tela à mão — o teste
 * automatizado força o erro pelo próprio teste, sem tocar aqui.
 */
const FALHAR: ReadonlySet<string> = new Set<string>();

export async function carregarDominio(chave: ChaveDeDominio): Promise<LinhaDeDominio[]> {
  await new Promise((resolve) => {
    setTimeout(resolve, LATENCIA_MS);
  });

  if (FALHAR.has(chave)) {
    throw new Error(`Falha simulada ao carregar "${chave}".`);
  }

  const colecao = simulados[chave];

  if (!Array.isArray(colecao)) {
    throw new Error(`Domínio desconhecido: "${chave}".`);
  }

  return colecao as LinhaDeDominio[];
}

/** Quantos registros ativos um domínio tem, para o contador do hub. */
export function contarAtivos(linhas: readonly LinhaDeDominio[]): number {
  return linhas.filter((linha) => linha.situacao !== 'inativo').length;
}

/** Total por domínio, sem carregar tela nenhuma — alimenta os cartões dos hubs. */
export function totalDoDominio(chave: ChaveDeDominio): number {
  const colecao = simulados[chave];
  return Array.isArray(colecao) ? colecao.length : 0;
}

/** Quantos registros ativos um domínio tem, pelo mesmo critério de `contarAtivos`. */
export function totalAtivoDoDominio(chave: ChaveDeDominio): number {
  const colecao = simulados[chave];
  return Array.isArray(colecao) ? contarAtivos(colecao as LinhaDeDominio[]) : 0;
}
